import {
    faAddressCard,
    faAlarmClock,
    faArrowDownToLine,
    faArrowRightArrowLeft,
    faBolt,
    faBoxesStacked,
    faBoxOpen,
    faBrain,
    faBuilding,
    faCalendarDays,
    faCartPlus,
    faChartColumn,
    faCircleCheck,
    faClockRotateLeft,
    faCopy,
    faDatabase,
    faEnvelope,
    faFileContract,
    faFileExcel,
    faFileInvoiceDollar,
    faFileLines,
    faFilePdf,
    faFileWord,
    faInbox,
    faLayerGroup,
    faMagnifyingGlass,
    faPaperclip,
    faPaperPlaneTop,
    faPen,
    faTag,
    faTrash,
    faTruckFast,
    faWrench,
} from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';

import { withIcon } from '../../core/components/Icon.tsx';
import { ASSISTANCE_PATH as DELIVERY_NOTE_ASSISTANCE_PATH } from '../../products/delivery_notes/constants.ts';
import { ASSISTANCE_PATH as INVOICE_ASSISTANCE_PATH } from '../../products/invoices/constants.ts';
import { ASSISTANCE_PATH as ORDER_CONFIRMATION_ASSISTANCE_PATH } from '../../products/order_confirmations/constants.ts';
import { ASSISTANCE_PATH as ORDER_ASSISTANCE_PATH } from '../../products/orders/constants.ts';
import { ASSISTANCE_PATH as RFQ_ASSISTANCE_PATH } from '../../products/rfqs/constants.ts';

export const ToolCallIcon = withIcon(faWrench);
export const AttachmentIcon = withIcon(faPaperclip);
export const CreateOrderIcon = withIcon(faCartPlus);
export const CreateRfqIcon = withIcon(faFileInvoiceDollar);
const PriceIcon = withIcon(faTag);
const TrackingIcon = withIcon(faTruckFast);
const DeliveryDateIcon = withIcon(faCalendarDays);
const AvailabilityIcon = withIcon(faBoxesStacked);
const ArticleSearchIcon = withIcon(faMagnifyingGlass);
const OrderHistoryIcon = withIcon(faClockRotateLeft);
const EmailIcon = withIcon(faEnvelope);
const AnalyticsToolIcon = withIcon(faChartColumn);
const MasterDataIcon = withIcon(faDatabase);
const RecordIcon = withIcon(faFileLines);
const InboxIcon = withIcon(faInbox);
const OfferIcon = withIcon(faFileContract);
const OpenOrdersIcon = withIcon(faBoxOpen);
const ClientSearchIcon = withIcon(faBuilding);
const ToolsTrashIcon = withIcon(faTrash);
const MemoryIcon = withIcon(faBrain);
const ContactIcon = withIcon(faAddressCard);
const EditIconForTools = withIcon(faPen);
const ExcelIcon = withIcon(faFileExcel);
const WordIcon = withIcon(faFileWord);
const PdfIcon = withIcon(faFilePdf);
export const ScheduleToolIcon = withIcon(faAlarmClock);
export const ToolsIcon = withIcon(faBolt);

const TOOL_ICONS: [RegExp, ReturnType<typeof withIcon>][] = [
    // ERP document + address-book tools first: their names contain words ("Delivery", "Price")
    // that the generic patterns below would otherwise claim
    [/ConvertErpTransaction/i, withIcon(faArrowRightArrowLeft)],
    [/CreateErpTransaction/i, CreateOrderIcon],
    [/UpdateErpTransaction/i, EditIconForTools],
    [/ListErpTransactions/i, OpenOrdersIcon],
    [/GetErpTransaction/i, OfferIcon],
    [/ErpContact/i, ContactIcon],
    [/ErpAddress|ErpPostalAddress/i, ClientSearchIcon],
    [/DownloadKnowledgeFiles/i, withIcon(faArrowDownToLine)],
    [/Excel/i, ExcelIcon],
    [/WordDoc/i, WordIcon],
    [/CreatePdf/i, PdfIcon],
    [/CreateOrderConfirmation/i, OfferIcon],
    [/CreateInvoice/i, CreateRfqIcon],
    [/CreateDeliveryNote/i, TrackingIcon],
    [/CreateOrder/i, CreateOrderIcon],
    [/CreateRfq/i, CreateRfqIcon],
    [/ReadInbox/i, InboxIcon],
    [/SendEmail|Email/i, EmailIcon],
    [/Analytics/i, AnalyticsToolIcon],
    [/MasterData|AccessArticleCatalog/i, MasterDataIcon],
    [/SearchClient/i, ClientSearchIcon],
    [/Offer/i, OfferIcon],
    [/OpenOrders/i, OpenOrdersIcon],
    [/Remember|Forget/i, MemoryIcon],
    [/DiscardRecord/i, ToolsTrashIcon],
    [/CreateRecordFromFile/i, CreateOrderIcon],
    [/CreateSchedule/i, ScheduleToolIcon],
    [/ListChannels/i, withIcon(faLayerGroup)],
    [/MoveRecordToChannel/i, withIcon(faArrowRightArrowLeft)],
    [/ReadRecord|ReadChatAttachment/i, RecordIcon],
    [/Price/i, PriceIcon],
    [/Tracking/i, TrackingIcon],
    [/Delivery/i, DeliveryDateIcon],
    [/Availability|Stock/i, AvailabilityIcon],
    [/FindArticle|Article/i, ArticleSearchIcon],
    [/History/i, OrderHistoryIcon],
];

export const getToolIcon = (name: string) => TOOL_ICONS.find(([pattern]) => pattern.test(name))?.[1] || ToolCallIcon;

// where a tool's result data comes from; anything not matched is a live ERP call
const TOOL_SOURCES: [RegExp, string][] = [
    [/Excel|WordDoc|CreatePdf|SplitPdf/i, 'sourceGenerated'],
    [/DownloadKnowledgeFiles/i, 'sourceKnowledge'],
    [/Analytics|History|Record|ListChannels|CreateSchedule|ReadChatAttachment/i, 'sourceWorkist'],
    [/FindArticle|SearchArticles|MasterData|AccessArticleCatalog|Offer|OpenOrders|SearchClient/i, 'sourceMasterData'],
    [/SendEmail|ReadInbox|MarkEmailAsRead/i, 'sourceSmartInbox'],
    [/Remember|Forget/i, 'sourceMemory'],
];

export const getToolSourceKey = (name: string) =>
    TOOL_SOURCES.find(([pattern]) => pattern.test(name))?.[1] || 'sourceErp';

// curated label/description locale keys for the tool grid; unmatched tools fall back to their docstring
const TOOL_META: [RegExp, string][] = [
    [/ConvertErpTransaction/i, 'erpConvertDocument'],
    [/CreateErpTransaction/i, 'erpCreateDocument'],
    [/UpdateErpTransaction/i, 'erpUpdateDocument'],
    [/ListErpTransactions/i, 'erpListDocuments'],
    [/GetErpTransaction/i, 'erpReadDocument'],
    [/CreateErpAddress/i, 'erpCreatePartner'],
    [/UpdateErpAddress/i, 'erpUpdatePartner'],
    [/CreateErpPostalAddress/i, 'erpCreateAddress'],
    [/UpdateErpPostalAddress/i, 'erpUpdateAddress'],
    [/CreateErpContact/i, 'erpCreateContact'],
    [/UpdateErpContact/i, 'erpUpdateContact'],
    [/CreateOrderConfirmation/i, 'createOrderConfirmation'],
    [/CreateInvoice/i, 'createInvoice'],
    [/CreateDeliveryNote/i, 'createDeliveryNote'],
    [/CreateOrder/i, 'createOrder'],
    [/CreateRfq/i, 'createRfq'],
    [/ReadInbox/i, 'readInbox'],
    [/SendEmail/i, 'sendEmail'],
    [/DownloadKnowledgeFiles/i, 'downloadKnowledgeFiles'],
    [/CreateSchedule/i, 'createSchedule'],
    [/Analytics/i, 'analytics'],
    [/AccessArticleCatalog/i, 'articleCatalog'],
    [/SearchClient/i, 'searchClient'],
    [/OpenOffers/i, 'openOffers'],
    [/OpenOrders/i, 'openOrders'],
    [/Price/i, 'price'],
    [/Tracking/i, 'tracking'],
    [/Delivery/i, 'deliveryDate'],
    [/Availability|Stock/i, 'availability'],
    [/SearchArticles/i, 'searchArticles'],
    [/FindArticle/i, 'findArticle'],
    [/History/i, 'orderHistory'],
];

export const getToolMetaKey = (name: string) => TOOL_META.find(([pattern]) => pattern.test(name))?.[1];

// per-tool empty-state message keys (thread.noData.*) for results that came back without any data
const TOOL_EMPTY_KEYS: [RegExp, string][] = [
    [/SearchClient/i, 'searchClient'],
    [/Price/i, 'price'],
    [/Tracking/i, 'tracking'],
    [/Delivery/i, 'deliveryDate'],
    [/Availability|Stock/i, 'availability'],
    [/FindArticle|Article|MasterData/i, 'masterData'],
    [/Offer/i, 'offers'],
    [/OpenOrders/i, 'orders'],
    [/History/i, 'orderHistory'],
    [/Analytics/i, 'analytics'],
];

export const getToolEmptyKey = (name: string) =>
    TOOL_EMPTY_KEYS.find(([pattern]) => pattern.test(name))?.[1] || 'default';

export const formatTimestamp = (value: string) => (value ? format(new Date(value), 'dd.MM.yy, HH:mm') : '');

// the price tool returns a Python (price, unit) tuple that serializes to e.g. "(3.74, 'Box')" — split it into
// a clean price/unit pair so the card can render labelled fields instead of the raw tuple string
export const parsePriceResult = (output): { price: string; unit: string } | null => {
    if (typeof output !== 'string') return null;
    const match = output.match(/^\(\s*([-\d.,]+)\s*,\s*['"]([^'"]*)['"]\s*\)$/);
    return match ? { price: match[1], unit: match[2] } : null;
};

export const parseToolCalls = (toolCalls): any[] => {
    if (Array.isArray(toolCalls)) return toolCalls;
    if (typeof toolCalls === 'string') {
        try {
            const parsed = JSON.parse(toolCalls);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

export const parseToolOutput = (toolOutput) => {
    if (typeof toolOutput !== 'string') return toolOutput;
    try {
        return JSON.parse(toolOutput);
    } catch {
        return toolOutput;
    }
};

// results from the ERP document tools: one Vorgang, or a list of them. They render as a document
// panel rather than through the generic row flattener, which would spread a document's partner,
// totals and positions over a dozen "line items 1 · article number" rows.
export const isErpDocumentOutput = (output): boolean =>
    Boolean(output && typeof output === 'object' && !Array.isArray(output) && output.transaction_number);

export const isErpDocumentListOutput = (output): boolean =>
    Boolean(
        output &&
            typeof output === 'object' &&
            Array.isArray(output.transactions) &&
            output.transactions.every((entry) => entry && typeof entry === 'object')
    );

// created_in_erp / updated_in_erp / converted_in_erp — the badge on a write result
export const ERP_WRITE_STATUSES = new Set(['created_in_erp', 'updated_in_erp', 'converted_in_erp']);

export const GENERATED_FILE_TOOL_PATTERN = /Excel|WordDoc|CreatePdf/i;
const GENERATED_FILE_TYPES = new Set(['excel_sheet', 'word_document', 'pdf_document']);

export const isGeneratedFileOutput = (output): boolean => GENERATED_FILE_TYPES.has(output?.type);

// knowledge files handed over for download (ENG-10870): each entry names a conversation attachment that
// references the knowledge file, resolved from the conversation's attachment list like an email's attachments
export const isKnowledgeFilesOutput = (output): boolean =>
    output?.type === 'knowledge_files' && Array.isArray(output.files);

const isEmptyToolOutput = (output) =>
    output == null || ['rows', 'documents', 'emails', 'files'].some((key) => output[key]?.length === 0);

export const partitionToolCalls = (toolCalls) => {
    const all = parseToolCalls(toolCalls);
    const generatedFiles = [];
    const trail = [];
    for (const toolCall of all) {
        if (toolCall.status === 'pending') continue;
        const output = parseToolOutput(toolCall.tool_output);
        if (CREATED_DOCUMENT_TOOL_PATTERN.test(toolCall.tool_name || '') && output?.status === 'created') continue;
        // a created schedule renders as its own card (ScheduleCreatedCard); a declined or superseded
        // proposal ("Not now", or saved through the full dialog instead) leaves no card behind
        if (
            CREATE_SCHEDULE_TOOL === toolCall.tool_name &&
            (output?.status === 'created' || toolCall.status === 'cancelled')
        )
            continue;
        if (isEmptyToolOutput(output)) continue;
        (isGeneratedFileOutput(output) ? generatedFiles : trail).push(toolCall);
    }
    return { all, pending: all.find((toolCall) => toolCall.status === 'pending') ?? null, generatedFiles, trail };
};

// attachment ids of files generated by tool calls in a message (Excel, Word, PDF) — they render as a
// download card, so they are excluded from the plain attachment chip list
export const extractGeneratedAttachmentIds = (toolCalls): Set<string> =>
    new Set(
        parseToolCalls(toolCalls)
            .filter((toolCall) => GENERATED_FILE_TOOL_PATTERN.test(toolCall.tool_name || ''))
            .map((toolCall) => parseToolOutput(toolCall.tool_output)?.attachment_id)
            .filter(Boolean)
    );

export const CREATED_DOCUMENT_META = {
    ORDER: {
        toolName: 'Order',
        path: ORDER_ASSISTANCE_PATH,
        icon: CreateOrderIcon,
        createdKey: 'thread.orderCreated',
        workbenchKey: 'thread.workbenchOrder',
    },
    REQUEST_FOR_QUOTATION: {
        toolName: 'Rfq',
        path: RFQ_ASSISTANCE_PATH,
        icon: CreateRfqIcon,
        createdKey: 'thread.rfqCreated',
        workbenchKey: 'thread.workbenchRfq',
    },
    ORDER_CONFIRMATION: {
        toolName: 'OrderConfirmation',
        path: ORDER_CONFIRMATION_ASSISTANCE_PATH,
        icon: OfferIcon,
        createdKey: 'thread.orderConfirmationCreated',
        workbenchKey: 'thread.workbenchOrderConfirmation',
    },
    INVOICE: {
        toolName: 'Invoice',
        path: INVOICE_ASSISTANCE_PATH,
        icon: CreateRfqIcon,
        createdKey: 'thread.invoiceCreated',
        workbenchKey: 'thread.workbenchInvoice',
    },
    DELIVERY_NOTE: {
        toolName: 'DeliveryNote',
        path: DELIVERY_NOTE_ASSISTANCE_PATH,
        icon: TrackingIcon,
        createdKey: 'thread.deliveryNoteCreated',
        workbenchKey: 'thread.workbenchDeliveryNote',
    },
};

const CREATED_DOCUMENT_TOOL_NAMES = Object.values(CREATED_DOCUMENT_META)
    .map((meta) => meta.toolName)
    .join('|');

export const CREATED_DOCUMENT_TOOL_PATTERN = new RegExp(
    `^Create(${CREATED_DOCUMENT_TOOL_NAMES})FromChat|^CreateRecordFromFile`
);

export const CREATE_SCHEDULE_TOOL = 'CreateScheduleToolClass';

// the scheduled tasks created by a message's confirmed CreateSchedule calls; the arguments ride along
// so the card can render a localized cadence instead of the backend's prose string
export const extractCreatedSchedules = (toolCalls): any[] =>
    parseToolCalls(toolCalls)
        .filter((toolCall) => toolCall.tool_name === CREATE_SCHEDULE_TOOL)
        .map((toolCall) => {
            const output = parseToolOutput(toolCall.tool_output);
            return output ? { ...output, args: toolCall.tool_input || {} } : null;
        })
        .filter((output) => output?.status === 'created' && output.schedule_id);

export const extractCreatedDocuments = (toolCalls): any[] =>
    parseToolCalls(toolCalls)
        .filter((toolCall) => CREATED_DOCUMENT_TOOL_PATTERN.test(toolCall.tool_name || ''))
        .map((toolCall) => {
            try {
                return JSON.parse(toolCall.tool_output);
            } catch {
                return null;
            }
        })
        .filter(
            (output) => output?.status === 'created' && output.record_id && CREATED_DOCUMENT_META[output.document_type]
        );

const ERP_PROVIDER_PREFIX =
    /^(DemoErp|Netsuite|SapBusinessOne|Sage100|Weclapp|BusinessCentral|Quickbooks|MicrotechBueroPlus|Microtech)/;

export const humanizeToolName = (name: string) =>
    name
        .replace(/ToolClass$/, '')
        .replace(ERP_PROVIDER_PREFIX, '')
        .replace(/FromChat$/, '')
        // "Erp" is a qualifier in the class name, not a word a user needs to read
        .replace(/Erp(Transactions?|Address|PostalAddress|Contacts?)$/, '$1')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();

export const toReadableArgKey = (key: string): string => {
    const spaced = key.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// SendEmail result statuses that mean an email was actually composed (vs. a guidance/error result)
export const EMAIL_COMPOSED_STATUSES = new Set(['sent', 'draft_created', 'reply_sent', 'reply_draft_created']);
const EMAIL_DRAFT_STATUSES = new Set(['draft_created', 'reply_draft_created']);

const SuggestAltIcon = withIcon(faArrowRightArrowLeft);
const PostIcon = withIcon(faCircleCheck);
const SendActionIcon = withIcon(faPaperPlaneTop);
const EditIcon = withIcon(faPen);
const CopyIcon = withIcon(faCopy);

// contextual follow-up buttons shown under a tool result; `promptKey` resolves to thread.actionPrompts.*
// (the message sent back to Worki), `copy: true` instead copies the composed email body to the clipboard
export type ToolResultAction = {
    key: string;
    Icon: ReturnType<typeof withIcon>;
    promptKey?: string;
    copy?: boolean;
};

const PRICE_ACTIONS: ToolResultAction[] = [
    { key: 'checkAvailability', Icon: AvailabilityIcon, promptKey: 'checkAvailability' },
    { key: 'createOffer', Icon: OfferIcon, promptKey: 'createOffer' },
    { key: 'orderDirectly', Icon: CreateOrderIcon, promptKey: 'orderDirectly' },
    { key: 'suggestAlternative', Icon: SuggestAltIcon, promptKey: 'suggestAlternative' },
];

const EMAIL_DRAFT_ACTIONS: ToolResultAction[] = [
    { key: 'sendEmail', Icon: SendActionIcon, promptKey: 'sendEmail' },
    { key: 'editDraft', Icon: EditIcon, promptKey: 'editDraft' },
    { key: 'copyDraft', Icon: CopyIcon, copy: true },
];

// A looked-up document is rarely the end of the question — the user wants to move it on, or write
// to whoever it belongs to. The actions are derived from the document itself rather than from the
// tool name, so one is never offered that the ERP would refuse: converting only where the ERP
// reports a target, e-mailing only where a contact actually has an address, posting only while the
// document is still open.
const hasContactWithEmail = (output) => (output?.partner?.contacts || []).some((contact) => Boolean(contact?.email));

const firstConversionTarget = (output) => {
    const targets = output?.can_be_converted_into;
    return Array.isArray(targets) && targets.length > 0 ? targets[0] : null;
};

const getDocumentActions = (output): ToolResultAction[] => {
    // a list answers about many documents at once; only the whole-result action makes sense there
    if (Array.isArray(output?.transactions)) {
        return hasContactWithEmail(output.transactions?.[0])
            ? [{ key: 'emailAboutDocuments', Icon: SendActionIcon, promptKey: 'emailAboutDocuments' }]
            : [];
    }
    // A read document always names its Vorgangsart. A call still awaiting confirmation renders its
    // ARGUMENTS here instead, and those carry a transaction_number but no type — without this, a
    // pending "post this document" would offer a Post button of its own.
    if (!output?.transaction_number || !output?.transaction_type) return [];

    const actions: ToolResultAction[] = [];
    const target = firstConversionTarget(output);
    if (target && !output.is_closed) {
        actions.push({ key: 'convertDocument', Icon: SuggestAltIcon, promptKey: 'convertDocument' });
    }
    if (hasContactWithEmail(output)) {
        actions.push({ key: 'emailAboutDocument', Icon: SendActionIcon, promptKey: 'emailAboutDocument' });
    }
    // posted is its own axis: a document can be open and already booked, and offering to post it
    // again would be refused by the ERP
    if (!output.is_posted) {
        actions.push({ key: 'postDocument', Icon: PostIcon, promptKey: 'postDocument' });
    }
    return actions;
};

export const getToolResultActions = (name: string, output): ToolResultAction[] => {
    if (!output || typeof output !== 'object' || Array.isArray(output) || output.error) return [];
    if (/Price/i.test(name)) return PRICE_ACTIONS;
    if (/SendEmail/i.test(name) && EMAIL_DRAFT_STATUSES.has(output.status)) return EMAIL_DRAFT_ACTIONS;
    // only the READING tools: a write tool's own result is not a document to act on again, and
    // offering "post" under a post call is nonsense
    if (/(Get|List)ErpTransactions?ToolClass/i.test(name)) return getDocumentActions(output);
    return [];
};

const TOOL_STATUS_KEYS: [RegExp, string][] = [
    [/Excel/i, 'statusExcel'],
    [/DownloadKnowledgeFiles/i, 'statusKnowledgeFiles'],
    [/CreateOrderConfirmation/i, 'statusCreateOrderConfirmation'],
    [/CreateInvoice/i, 'statusCreateInvoice'],
    [/CreateDeliveryNote/i, 'statusCreateDeliveryNote'],
    [/CreateOrder/i, 'statusCreateOrder'],
    [/CreateRfq/i, 'statusCreateRfq'],
    [/ReadInbox/i, 'statusReadInbox'],
    [/SendEmail/i, 'statusSendEmail'],
    [/Analytics/i, 'statusAnalytics'],
    [/AccessArticleCatalog/i, 'statusArticleCatalog'],
    [/SearchClient/i, 'statusSearchClient'],
    [/OpenOffers/i, 'statusOpenOffers'],
    [/OpenOrders/i, 'statusOpenOrders'],
    [/DiscardRecord/i, 'statusDiscardRecord'],
    [/CreateRecordFromFile/i, 'statusCreateRecord'],
    [/CreateSchedule/i, 'statusCreateSchedule'],
    [/ReadRecord/i, 'statusReadRecord'],
    [/Price/i, 'statusPrice'],
    [/Tracking/i, 'statusTracking'],
    [/Delivery/i, 'statusDeliveryDate'],
    [/Availability|Stock/i, 'statusAvailability'],
    [/SearchArticles/i, 'statusSearchArticles'],
    [/FindArticle/i, 'statusFindArticle'],
    [/History/i, 'statusOrderHistory'],
    [/MarkEmailAsRead/i, 'statusMarkRead'],
];

export const getToolStatusKey = (name: string) =>
    `thread.tools.${TOOL_STATUS_KEYS.find(([pattern]) => pattern.test(name))?.[1] ?? 'statusDefault'}`;

const TOOL_STEP_KEYS: [RegExp, string][] = [
    [/Excel/i, 'stepExcel'],
    [/DownloadKnowledgeFiles/i, 'stepKnowledgeFiles'],
    [/CreateOrderConfirmation/i, 'stepCreateOrderConfirmation'],
    [/CreateInvoice/i, 'stepCreateInvoice'],
    [/CreateDeliveryNote/i, 'stepCreateDeliveryNote'],
    [/CreateOrder/i, 'stepCreateOrder'],
    [/CreateRfq/i, 'stepCreateRfq'],
    [/ReadInbox/i, 'stepReadInbox'],
    [/SendEmail/i, 'stepSendEmail'],
    [/Analytics/i, 'stepAnalytics'],
    [/AccessArticleCatalog/i, 'stepArticleCatalog'],
    [/SearchClient/i, 'stepSearchClient'],
    [/OpenOffers/i, 'stepOpenOffers'],
    [/OpenOrders/i, 'stepOpenOrders'],
    [/DiscardRecord/i, 'stepDiscardRecord'],
    [/CreateRecordFromFile/i, 'stepCreateRecord'],
    [/CreateSchedule/i, 'stepCreateSchedule'],
    [/ReadRecord/i, 'stepReadRecord'],
    [/Price/i, 'stepPrice'],
    [/Tracking/i, 'stepTracking'],
    [/Delivery/i, 'stepDeliveryDate'],
    [/Availability|Stock/i, 'stepAvailability'],
    [/SearchArticles/i, 'stepSearchArticles'],
    [/FindArticle/i, 'stepFindArticle'],
    [/History/i, 'stepOrderHistory'],
    [/MarkEmailAsRead/i, 'stepMarkRead'],
];

export const getToolStepKey = (name: string) =>
    `thread.tools.${TOOL_STEP_KEYS.find(([pattern]) => pattern.test(name))?.[1] ?? 'stepDefault'}`;

const TOOL_UNAVAILABLE_KEYS: [RegExp, string][] = [
    [/Price|Tracking|Delivery|Availability|Stock/i, 'unavailableErp'],
    [/FindArticle|SearchArticles|AccessArticleCatalog|MasterData/i, 'unavailableMasterData'],
    [/SendEmail|ReadInbox|MarkEmailAsRead/i, 'unavailableEmail'],
    [/History/i, 'unavailableOrderHistory'],
    [/Analytics/i, 'unavailableAnalytics'],
    [/Excel/i, 'unavailableExcel'],
    [
        /CreateOrder|CreateRfq|CreateRecordFromFile|CreateOrderConfirmation|CreateInvoice|CreateDeliveryNote/i,
        'unavailableCreate',
    ],
];

export const getToolUnavailableKey = (name: string) =>
    `thread.tools.${TOOL_UNAVAILABLE_KEYS.find(([pattern]) => pattern.test(name))?.[1] ?? 'unavailableDefault'}`;

export const isToolOutputError = (toolOutput: unknown): boolean => {
    const parsed = parseToolOutput(toolOutput);
    return Boolean(parsed && typeof parsed === 'object' && 'error' in parsed);
};
